// Search for the cached dropdown lists (vendors, products, users, brands …).
// A big list opens with its first rows (never thousands) plus a hint header,
// and each option matches on several fields — not only its visible label:
//   vendors  → name / alias, ID, GSTIN
//   products → name, barcode, brand
//   users    → name, username, phone
//   brands   → name, ID
// Every typed word must appear somewhere ("maggi 70" finds "Maggi Noodles
// 70g"). Exact ID / barcode hits come first.

const get = (obj, path) =>
  path.split(".").reduce((v, k) => (v == null ? v : v[k]), obj);

// Fields read when present on an option (missing ones are simply skipped).
const SEARCH_FIELDS = [
  "id",
  "name",
  "alias",
  "companyName",
  "GST",
  "GSTIN",
  "gstin",
  "barcode",
  "brandName",
  "brands.brandName",
  "brands.id",
  "brandId",
  "brand.brandName",
  "brand.name",
  "brand.id",
  "userName",
  "contactNumber",
  "phoneNumber",
  "phone",
  "storename",
  "categoryName",
  "warehouseNickname",
  // property owners ({ id, user: { … } })
  "user.id",
  "user.name",
  "user.userName",
  "user.contactNumber",
  "user.phoneNumber",
];

// Exact hits on these rank first (a scanned barcode, a typed ID / GSTIN).
const EXACT_FIELDS = [
  "id",
  "barcode",
  "GST",
  "GSTIN",
  "gstin",
  "userName",
  "contactNumber",
  "user.id",
  "user.userName",
  "user.contactNumber",
];

const norm = (v) => `${v ?? ""}`.toLowerCase().trim();

// One search string per option object, built once (lists can be 1 lakh+).
const haystacks = new WeakMap();
const haystackOf = (option, getOptionLabel) => {
  if (option && typeof option === "object" && haystacks.has(option)) return haystacks.get(option);
  let label = "";
  try {
    label = getOptionLabel ? getOptionLabel(option) : "";
  } catch (e) {
    label = "";
  }
  const parts = [label];
  if (option && typeof option === "object") {
    SEARCH_FIELDS.forEach((f) => {
      const v = get(option, f);
      if (v !== undefined && v !== null && v !== "") parts.push(v);
    });
  } else {
    parts.push(option);
  }
  const text = parts.map(norm).join(" | ");
  if (option && typeof option === "object") haystacks.set(option, text);
  return text;
};

/**
 * What kind of list this is, from its first row — used for the hint.
 * Most specific first: stores also carry a GSTIN, so the vendor check
 * (alias / GST) must come last.
 */
export const detectListKind = (options) => {
  const o = Array.isArray(options) ? options.find(Boolean) : null;
  if (!o || typeof o !== "object") return null;
  if ("barcode" in o) return "product";
  if ("storename" in o) return "store";
  if (o.user && typeof o.user === "object") return "owner";
  if ("userName" in o) return "user";
  if ("brandName" in o) return "brand";
  if ("alias" in o || "GST" in o || "GSTIN" in o) return "vendor";
  return null;
};

export const SEARCH_HINTS = {
  product: "Type to search by name, barcode and Id",
  vendor: "Type to search by name, ID or GSTIN",
  user: "Type to search by name, username or phone",
  brand: "Type to search by name or ID",
  store: "Type to search by store name or ID",
  owner: "Type to search by owner name, username, phone or ID",
};

// Lists up to this size open fully on click; bigger ones open with their
// first PREVIEW_LIMIT rows and a "Showing 50 of N · type to search" header.
export const SHOW_ALL_LIMIT = 100;
export const PREVIEW_LIMIT = 50;
const RESULT_LIMIT = 100;
const SCAN_CAP = 3000; // stop collecting after this many hits, then rank

/**
 * React key for an option. MUI keys rows by their label, so two rows with
 * the same label (same brand / vendor / owner listed twice) make React reuse
 * the wrong rows while typing — wrong names and blank lines. Key by the id
 * plus the identifying fields instead; rows equal in all of them are the
 * same row (and are dropped from the results, see smartFilter).
 */
export const optionKey = (option) => {
  if (option === null || option === undefined) return "";
  if (typeof option !== "object") return `v:${option}`;
  const parts = [
    option.id,
    get(option, "user.id"),
    option.barcode,
    option.name,
    option.alias,
    option.brandName,
    option.storename,
    option.userName,
    get(option, "user.name"),
    get(option, "user.userName"),
  ];
  return `o:${parts.map((p) => (p === undefined || p === null ? "" : `${p}`)).join("|")}`;
};

// Same primitive values on every top-level field → a true duplicate.
// Rows that only share the key (e.g. two stock batches of one product with
// different prices) are kept.
const samePrimitives = (a, b) => {
  if (a === b) return true;
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const x = a[k];
    const y = b[k];
    if ((x !== null && typeof x === "object") || (y !== null && typeof y === "object")) continue;
    if (x !== y) return false;
  }
  return true;
};

const uniqueByKey = (list) => {
  const seen = new Map();
  return list.filter((o) => {
    const k = optionKey(o);
    const prev = seen.get(k);
    if (prev && prev.some((p) => samePrimitives(p, o))) return false;
    seen.set(k, prev ? [...prev, o] : [o]);
    return true;
  });
};

/**
 * filterOptions for MUI Autocomplete.
 * @param getOptionLabel the field's label function (also searched); when
 *   omitted, the label function MUI passes in `state` is used — which lets
 *   this be the app-wide default for every Autocomplete (see theme/index.js).
 * Empty input: the whole list when it's short, else its first PREVIEW_LIMIT
 * rows — real data on open, never thousands of rows.
 */
export const smartFilter = (getOptionLabel) => (options = [], state = {}) => {
  const labelOf = getOptionLabel || state.getOptionLabel;
  const query = norm(state.inputValue);
  if (!query) {
    return uniqueByKey(options.length <= SHOW_ALL_LIMIT ? options : options.slice(0, PREVIEW_LIMIT));
  }

  const tokens = query.split(/\s+/).filter(Boolean);
  const hits = [];
  for (let i = 0; i < options.length && hits.length < SCAN_CAP; i++) {
    const option = options[i];
    const text = haystackOf(option, labelOf);
    if (tokens.every((t) => text.includes(t))) hits.push(option);
  }

  const rank = (option) => {
    if (option && typeof option === "object") {
      if (EXACT_FIELDS.some((f) => norm(get(option, f)) === query)) return 0;
    }
    let label = "";
    try {
      label = norm(labelOf ? labelOf(option) : option);
    } catch (e) {
      label = "";
    }
    if (label.startsWith(query)) return 1;
    if (label.includes(query)) return 2;
    return 3;
  };

  return uniqueByKey(hits)
    .map((option, i) => ({ option, r: rank(option), i }))
    // stable: equal ranks keep the list's own order
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .slice(0, RESULT_LIMIT)
    .map((x) => x.option);
};
